import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useAppContext } from '../../contexts/Storage';
import { TransactionItem } from '../../models/TransactionItem';
import { TransactionEntity } from '../../models/TransactionEntity';
import { TransactionFetchFilter } from '../../models/TransactionFetchFilter';
import {
  transactionsService,
  TransactionsApiResponse,
  CreateTransactionData,
  CreateTransactionApiResponse,
  UpdateTransactionData,
  mapApiTransactionToItem,
} from '../../services/transactionsService';
import {
  transactionEntitiesService,
  CreateTransactionEntityData,
  CreateTransactionEntityApiResponse,
  UpdateTransactionEntityData,
  FindTransactionEntitiesBody,
  FindTransactionEntitiesApiResponse,
  mapApiEntityToTransactionEntity,
} from '../../services/transactionEntitiesService';
import { unwrapApiResult } from '../../lib/react-query';

// ---------------------------------------------------------------------------
// Query keys & types 
// ---------------------------------------------------------------------------

export const TRANSACTIONS_QUERY_KEY = ['transactions'] as const;
export const TRANSACTION_ENTITIES_QUERY_KEY = ['transaction-entities'] as const;

export const CREATE_TRANSACTION_MUTATION_KEY = ['create-transaction'] as const;
export const UPDATE_TRANSACTION_MUTATION_KEY = ['update-transaction'] as const;
export const DELETE_TRANSACTION_MUTATION_KEY = ['delete-transaction'] as const;
export const CREATE_TRANSACTION_ENTITY_MUTATION_KEY = ['create-transaction-entity'] as const;
export const UPDATE_TRANSACTION_ENTITY_MUTATION_KEY = ['update-transaction-entity'] as const;
export const DELETE_TRANSACTION_ENTITY_MUTATION_KEY = ['delete-transaction-entity'] as const;

export interface TransactionsPagination {
  page: number;
  limit: number;
  total: number;
  lastPage?: number;
  from?: number;
  to?: number;
}

export interface TransactionsResponse {
  transactions: TransactionItem[];
  pagination?: TransactionsPagination;
}

export type TransactionFilters = Partial<TransactionFetchFilter> & {
  search?: string;
};

export interface TransactionEntitiesPagination {
  page: number;
  limit: number;
  total: number;
  lastPage: number;
  from: number | null;
  to: number | null;
}

export interface TransactionEntitiesResponse {
  entities: TransactionEntity[];
  pagination: TransactionEntitiesPagination;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export interface UseGetTransactionsParams {
  filters?: TransactionFetchFilter;
  enabled?: boolean;
}

/**
 * Fetches transactions for the current user with optional filters.
 * Requires record_id in filters (API requirement).
 */
export function useGetTransactions({
  filters,
  enabled = true,
}: UseGetTransactionsParams = {}): UseQueryResult<TransactionsResponse, Error> {
  const { token, user } = useAppContext();

  return useQuery({
    queryKey: [...TRANSACTIONS_QUERY_KEY, user?.id, filters?.toQueryParams?.() ?? {}],
    queryFn: async () => {
      if (!token || !user?.id) {
        throw new Error('User not authenticated');
      }

      const result = await transactionsService.getTransactions(token, user.id, filters);
      const apiResponse = unwrapApiResult<TransactionsApiResponse>(result);

      const rawTransactions = apiResponse?.transactions?.data ?? [];
      const transactions: TransactionItem[] = rawTransactions.map(mapApiTransactionToItem);

      const paginationData = apiResponse?.transactions;

      return {
        transactions,
        pagination: {
          page: paginationData?.current_page ?? 1,
          limit: paginationData?.per_page ?? 20,
          total: paginationData?.total ?? transactions.length,
          lastPage: paginationData?.last_page ?? 1,
          from: paginationData?.from ?? 0,
          to: paginationData?.to ?? 0,
        },
      };
    },
    enabled: enabled && !!token && !!user?.id,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Transaction Entities Query
// ---------------------------------------------------------------------------

export interface UseGetTransactionEntitiesParams {
  filters?: FindTransactionEntitiesBody;
  enabled?: boolean;
}

/**
 * Fetches transaction entities for the current user with optional filters (POST find API).
 */
export function useGetTransactionEntities({
  filters,
  enabled = true,
}: UseGetTransactionEntitiesParams = {}): UseQueryResult<TransactionEntitiesResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  return useQuery({
    queryKey: [...TRANSACTION_ENTITIES_QUERY_KEY, user?.id, selectedRecord?.id, filters ?? {}],
    queryFn: async () => {
      if (!token || !user?.id) {
        throw new Error('User not authenticated');
      }

      const payload: FindTransactionEntitiesBody = {
        ...filters,
        record_id: filters?.record_id ?? selectedRecord?.id,
      };

      const result = await transactionEntitiesService.findTransactionEntities(
        token,
        user.id,
        payload
      );
      const apiResponse = unwrapApiResult<FindTransactionEntitiesApiResponse>(result);

      const rawData = apiResponse?.data ?? [];
      const entities = rawData.map(mapApiEntityToTransactionEntity);

      return {
        entities,
        pagination: {
          page: apiResponse?.current_page ?? 1,
          limit: apiResponse?.per_page ?? 20,
          total: apiResponse?.total ?? 0,
          lastPage: apiResponse?.last_page ?? 1,
          from: apiResponse?.from ?? null,
          to: apiResponse?.to ?? null,
        },
      };
    },
    enabled: enabled && !!token && !!user?.id,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface DeleteTransactionParams {
  transactionId: number;
}

export function useDeleteTransaction() {
  const { token } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DELETE_TRANSACTION_MUTATION_KEY,
    mutationFn: async ({ transactionId }: DeleteTransactionParams) => {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await transactionsService.deleteTransaction(token, transactionId);
      unwrapApiResult(result);

      return { transactionId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_QUERY_KEY });
      toast.success('Transaction deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete transaction');
    },
  });
}

// ---------------------------------------------------------------------------
// Create Transaction Mutation
// ---------------------------------------------------------------------------

export interface CreateTransactionParams {
  data: CreateTransactionData;
}

export interface CreateTransactionResult {
  transaction: CreateTransactionApiResponse['transaction'];
}

/**
 * Creates a new transaction. Invalidates transactions list on success.
 */
export function useCreateTransaction(): UseMutationResult<
  CreateTransactionResult,
  Error,
  CreateTransactionParams
> {
  const { token, user } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CREATE_TRANSACTION_MUTATION_KEY,
    mutationFn: async ({ data }: CreateTransactionParams) => {
      if (!token || !user?.id) {
        throw new Error('User not authenticated');
      }

      const result = await transactionsService.createTransaction(token, user.id, data);
      const apiResponse = unwrapApiResult<CreateTransactionApiResponse>(result);

      return { transaction: apiResponse.transaction };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_QUERY_KEY });
      toast.success('Transaction created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create transaction');
    },
  });
}

// ---------------------------------------------------------------------------
// Update Transaction Mutation
// ---------------------------------------------------------------------------

export interface UpdateTransactionParams {
  transactionId: number;
  data: UpdateTransactionData;
}

export interface UpdateTransactionResult {
  transaction: CreateTransactionApiResponse['transaction'];
}

/**
 * Updates a transaction. PUT /api/transactions/{transaction_id}/{user_id}. Invalidates transactions list on success.
 */
export function useUpdateTransaction(): UseMutationResult<
  UpdateTransactionResult,
  Error,
  UpdateTransactionParams
> {
  const { token, user } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: UPDATE_TRANSACTION_MUTATION_KEY,
    mutationFn: async ({ transactionId, data }: UpdateTransactionParams) => {
      if (!token || !user?.id) {
        throw new Error('User not authenticated');
      }

      const result = await transactionsService.updateTransaction(
        token,
        transactionId,
        user.id,
        data
      );
      const transaction = unwrapApiResult<CreateTransactionApiResponse['transaction']>(result);
      return { transaction };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_QUERY_KEY });
      toast.success('Transaction updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update transaction');
    },
  });
}

// ---------------------------------------------------------------------------
// Create Transaction Entity Mutation
// ---------------------------------------------------------------------------

export interface CreateTransactionEntityParams {
  data: CreateTransactionEntityData;
}

export interface CreateTransactionEntityResult {
  entity: TransactionEntity;
}

/**
 * Creates a new transaction entity. Invalidates transaction entities list on success.
 */
export function useCreateTransactionEntity(): UseMutationResult<
  CreateTransactionEntityResult,
  Error,
  CreateTransactionEntityParams
> {
  const { token, user } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CREATE_TRANSACTION_ENTITY_MUTATION_KEY,
    mutationFn: async ({ data }: CreateTransactionEntityParams) => {
      if (!token || !user?.id) {
        throw new Error('User not authenticated');
      }

      const result = await transactionEntitiesService.createTransactionEntity(
        token,
        user.id,
        data
      );
      const apiResponse = unwrapApiResult<CreateTransactionEntityApiResponse>(result);

      // Map API response to TransactionEntity model
      const entity = new TransactionEntity({
        id: apiResponse.entity.id,
        transaction_entity_name: apiResponse.entity.transaction_entity_name,
        transaction_entity_description: apiResponse.entity.transaction_entity_description ?? undefined,
        transaction_entity_category: apiResponse.entity.transaction_entity_category,
        transaction_entity_type: apiResponse.entity.transaction_entity_type ?? undefined,
        transaction_entity_past_tense: apiResponse.entity.transaction_entity_past_tense ?? undefined,
      });

      return { entity };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSACTION_ENTITIES_QUERY_KEY });
      toast.success('Transaction entity created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create transaction entity');
    },
  });
}

// ---------------------------------------------------------------------------
// Update Transaction Entity Mutation
// ---------------------------------------------------------------------------

export interface UpdateTransactionEntityParams {
  entityId: number;
  data: UpdateTransactionEntityData;
}

export interface UpdateTransactionEntityResult {
  entity: TransactionEntity;
}

/**
 * Updates a transaction entity. Invalidates transaction entities list on success.
 */
export function useUpdateTransactionEntity(): UseMutationResult<
  UpdateTransactionEntityResult,
  Error,
  UpdateTransactionEntityParams
> {
  const { token, selectedRecord } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: UPDATE_TRANSACTION_ENTITY_MUTATION_KEY,
    mutationFn: async ({ entityId, data }: UpdateTransactionEntityParams) => {
      if (!token) {
        throw new Error('User not authenticated');
      }

      const payload: UpdateTransactionEntityData = {
        ...data,
        record_id: data.record_id ?? selectedRecord?.id,
      };

      const result = await transactionEntitiesService.updateTransactionEntity(token, entityId, payload);
      const raw = unwrapApiResult<TransactionEntity>(result);

      const entity = new TransactionEntity({
        id: raw.id,
        transaction_entity_name: raw.transaction_entity_name,
        transaction_entity_description: raw.transaction_entity_description ?? undefined,
        transaction_entity_category: raw.transaction_entity_category,
        transaction_entity_type: raw.transaction_entity_type ?? undefined,
        transaction_entity_past_tense: raw.transaction_entity_past_tense ?? undefined,
      });

      return { entity };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSACTION_ENTITIES_QUERY_KEY });
      toast.success('Transaction entity updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update transaction entity');
    },
  });
}

// ---------------------------------------------------------------------------
// Delete Transaction Entity Mutation
// ---------------------------------------------------------------------------

export interface DeleteTransactionEntityParams {
  entityId: number;
}

/**
 * Deletes a transaction entity. Invalidates transaction entities list on success.
 */
export function useDeleteTransactionEntity(): UseMutationResult<
  { entityId: number },
  Error,
  DeleteTransactionEntityParams
> {
  const { token } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DELETE_TRANSACTION_ENTITY_MUTATION_KEY,
    mutationFn: async ({ entityId }: DeleteTransactionEntityParams) => {
      if (!token) {
        throw new Error('User not authenticated');
      }

      const result = await transactionEntitiesService.deleteTransactionEntity(token, entityId);
      unwrapApiResult(result);
      return { entityId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSACTION_ENTITIES_QUERY_KEY });
      toast.success('Transaction entity deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete transaction entity');
    },
  });
}
